// Global Instructions Rule Applied!
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../state_manager/store.ts'
import type { UserPreferenceReportModel } from '../../state_manager/data_models/user_preference_report_model.ts'
import {
  getMenuItemIdByKey,
  getUserPreferenceForMenuItem,
  persistMenuItemPreference,
  deleteMenuItemPreference,
  type PersistUserPreferenceResult,
  type MenuItemKeyRow
} from './userPreferences.ts'

/**
 * Subscribe to global-config menu item + `user_preferences_reports` row for a given `menu_items.key`.
 * Exposes a stable {@link persistPreference} that resolves `menu_item_id` from current `menu_items`.
 * `preferredMenuItemId` pins the row to one copy of a key that sits in several
 * menu categories (see {@link getMenuItemIdByKey}).
 */
export function useMenuItemUserPreference(
  menuItemKey: string,
  preferredMenuItemId?: string | null
): {
  menuItemId: string | undefined
  preferenceRow: UserPreferenceReportModel | undefined
  menuItems: MenuItemKeyRow[]
  persistPreference: (reportId: string | undefined, data?: Record<string, unknown>) => Promise<PersistUserPreferenceResult>
  deletePreference: () => Promise<PersistUserPreferenceResult>
} {
  const menuItems = useSelector((state: RootState) => state.globalConfigs?.menu_items ?? []) as MenuItemKeyRow[]

  const userPreferencesReports = useSelector(
    (state: RootState) => state.globalConfigs?.user_preferences_reports ?? []
  ) as UserPreferenceReportModel[]

  const menuItemId = useMemo(
    () => getMenuItemIdByKey(menuItems, menuItemKey, preferredMenuItemId),
    [menuItems, menuItemKey, preferredMenuItemId]
  )

  const preferenceRow = useMemo(
    () => getUserPreferenceForMenuItem(userPreferencesReports, menuItemId),
    [userPreferencesReports, menuItemId]
  )

  const persistPreference = useCallback(
    (reportId: string | undefined, data: Record<string, unknown> = {}) =>
      persistMenuItemPreference(menuItems, menuItemKey, reportId, data, preferredMenuItemId),
    [menuItems, menuItemKey, preferredMenuItemId]
  )

  const deletePreference = useCallback(
    () => deleteMenuItemPreference(menuItems, menuItemKey, preferredMenuItemId),
    [menuItems, menuItemKey, preferredMenuItemId]
  )

  return { menuItemId, preferenceRow, menuItems, persistPreference, deletePreference }
}
